/**
 * Generates a Gaussian kernel for blurring images.
 * @param {number} size - The size of the kernel
 * @param {number} sigma - The standard deviation for the Gaussian function
 * @returns {number[]} The Gaussian kernel
 */
export function generateGaussianKernel(size, sigma) {
  const kernel = [];
  let sum = 0;
  const halfSize = Math.floor(size / 2);

  for (let y = -halfSize; y <= halfSize; y++) {
    for (let x = -halfSize; x <= halfSize; x++) {
      const value =
        (1 / (2 * Math.PI * sigma ** 2)) *
        Math.exp(-(x ** 2 + y ** 2) / (2 * sigma ** 2));
      kernel.push(value);
      sum += value;
    }
  }

  return kernel.map((v) => v / sum);
}

/**
 * Applies Gaussian blur to an image.
 * @param {Uint8ClampedArray} imageData - The image data to blur
 * @param {number} width - The width of the image
 * @param {number} height - The height of the image
 * @param {number} kernelSize - The size of the Gaussian kernel
 * @param {number} sigma - The standard deviation for the Gaussian function
 * @returns {Uint8ClampedArray} The blurred image data
 */
export function applyGaussianBlur(imageData, width, height, kernelSize, sigma) {
  const kernel = generateGaussianKernel(kernelSize, sigma);
  const halfSize = Math.floor(kernelSize / 2);
  const output = new Uint8ClampedArray(imageData.length);

  function getPixel(x, y, channel) {
    if (x < 0 || x >= width || y < 0 || y >= height) return 0;
    return imageData[(y * width + x) * 4 + channel];
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      for (let c = 0; c < 3; c++) {
        // RGB channels
        let sum = 0;
        let k = 0;

        for (let ky = -halfSize; ky <= halfSize; ky++) {
          for (let kx = -halfSize; kx <= halfSize; kx++) {
            sum += getPixel(x + kx, y + ky, c) * kernel[k];
            k++;
          }
        }

        output[(y * width + x) * 4 + c] = sum;
      }
      // Preserve alpha channel
      output[(y * width + x) * 4 + 3] = getPixel(x, y, 3);
    }
  }

  return output;
}
